import React, { useEffect, useState } from 'react';
import { filterUsers, restoreUser, UserFilterParams } from '../api/Users';
import { getAllLicStates } from '../api/States';
import { getAllProfessions, Profession } from '../api/Professions';
import { runAutoArchive } from '../api/Classes';
import { useNavigate } from 'react-router-dom';
import { User, LicState } from '../types';

const AdminPanel: React.FC = () => {
  const navigate = useNavigate();
  
  // User data
  const [users, setUsers] = useState<User[]>([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, totalPages: 0 });
  const [stats, setStats] = useState({ totalUsers: 0, activeUsers: 0, suspendedUsers: 0, filteredCount: 0 });

  // Filter states
  const [filters, setFilters] = useState<UserFilterParams>({
    page: 1,
    limit: 20,
    sortBy: 'lastLoginAt',
    sortOrder: 'desc',
  });
  
  // Dropdown data
  const [states, setStates] = useState<LicState[]>([]);
  const [professions, setProfessions] = useState<Profession[]>([]);
  
  // UI states
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [toast, setToast] = useState<{ type: 'success' | 'danger' | 'info'; message: string } | null>(null);
  const [restoringId, setRestoringId] = useState<string | null>(null);

  // Load states and professions on mount
  useEffect(() => {
    const loadDropdownData = async () => {
      try {
        const [statesData, professionsData] = await Promise.all([
          getAllLicStates(),
          getAllProfessions(),
        ]);
        setStates(statesData);
        setProfessions(professionsData);
      } catch (err: any) {
        console.error('Failed to load dropdown data:', err);
      }
    };
    loadDropdownData();
  }, []);

  // Load users whenever filters change
  useEffect(() => {
    loadUsers();
  }, [filters]);

  const loadUsers = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await filterUsers(filters);
      setUsers(response.users);
      setPagination(response.pagination);
      setStats(response.stats);
    } catch (err: any) {
      setError('Failed to load users.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const showToast = (type: 'success' | 'danger' | 'info', message: string) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 3000);
  };

const handleFilterChange = (key: keyof UserFilterParams, value: any) => {
    setFilters(prev => ({
      ...prev,
      [key]: value === '' ? undefined : value,
      page: key !== 'page' ? 1 : prev.page, // Reset to page 1 when changing filters (except pagination)
    }));
  };

  const clearFilters = () => {
    setFilters({
      page: 1,
      limit: 20,
      sortBy: 'lastLoginAt',
      sortOrder: 'desc',
    });
  };

  const handlePageChange = (newPage: number) => {
    setFilters(prev => ({ ...prev, page: newPage }));
  };

  const handleRestoreUser = async (e: React.MouseEvent, userId: string) => {
    e.stopPropagation();
    if (!window.confirm('Restore this user? They will be able to log in again.')) return;
    setRestoringId(userId);
    try {
      await restoreUser(userId);
      showToast('success', 'User restored successfully.');
      await loadUsers();
    } catch (err: any) {
      showToast('danger', 'Failed to restore user: ' + (err.response?.data?.error || err.message));
    } finally {
      setRestoringId(null);
    }
  };

  const handleAutoArchive = async () => {
    if (!window.confirm('Run auto-archive? This will mark historical classes (before earliest active cycle, completed, not assigned to any cycle) as archived.')) {
      return;
    }
    
    setLoading(true);
    try {
      const result = await runAutoArchive();
      showToast('success', `${result.message} (${result.archivedCount} classes)`);
    } catch (err: any) {
      console.error('Auto-archive error:', err);
      showToast('danger', 'Failed to run auto-archive: ' + (err.response?.data?.error || err.message));
    } finally {
      setLoading(false);
    }
  };

  const exportToCSV = () => {
    const headers = ['Email', 'First Name', 'Last Name', 'Role', 'Status', 'Registered'];
    const rows = users.map(u => [
      u.email || '',
      u.firstName || '',
      u.lastName || '',
      u.role || 'user',
      u.suspended ? 'Suspended' : 'Active',
      u.createdAt ? new Date(u.createdAt).toLocaleDateString() : '',
    ]);
    
    const csvContent = [headers, ...rows]
      .map(row => row.map(cell => `"${cell}"`).join(','))
      .join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `users_export_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  const activeFilters = Object.entries(filters).filter(([key, value]) =>
    value !== undefined && !['page', 'limit', 'sortBy', 'sortOrder'].includes(key)
  );

  const PROFESSION_COLORS: Record<string, { bg: string; color: string }> = {
    ARCH:  { bg: '#10B981', color: '#fff'    },
    PE:    { bg: '#182745', color: '#fff'    },
    CPA:   { bg: '#F59E0B', color: '#fff'    },
    LAW:   { bg: '#2D3748', color: '#fff'    },
    DENT:  { bg: '#EF4444', color: '#fff'    },
    DDS:   { bg: '#EF4444', color: '#fff'    },
    PHARM: { bg: '#FFCD00', color: '#182745' },
    LCSW:  { bg: '#8BF559', color: '#182745' },
    COUNS: { bg: '#8BF559', color: '#182745' },
    RE:    { bg: '#E2F6EF', color: '#065f46' },
    LMHC:  { bg: '#FEF3C7', color: '#92400e' },
  };
  const getProfessionBadgeStyle = (pid: string | undefined) => {
    if (!pid) return { bg: '#EDE8DE', color: '#374151' };
    const key = pid.toUpperCase().replace(/^[A-Z]{2}-/, '');
    return PROFESSION_COLORS[key] || PROFESSION_COLORS[pid.toUpperCase()] || { bg: '#EDE8DE', color: '#374151' };
  };

  const fmtDateTime = (val: string | Date | undefined) => {
    if (!val) return 'N/A';
    const d = new Date(val);
    if (isNaN(d.getTime())) return 'N/A';
    return d.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' })
      + ' ' + d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false });
  };

  return (
    <div className="container-fluid mt-4" style={{ maxWidth: 1400, margin: '0 auto' }}>
      {/* Toast notification */}
      {toast && (
        <div 
          className={`alert alert-${toast.type} position-fixed`} 
          role="alert" 
          style={{ top: 20, right: 20, zIndex: 1050, minWidth: 280 }}
        >
          {toast.message}
        </div>
      )}

      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>Admin Panel - User Management</h2>
        <div>
          <button className="btn btn-success me-2" onClick={() => navigate('/admin/users/add')}>
            Add User
          </button>
          <button className="btn btn-primary me-2" onClick={() => navigate('/states')}>
            Manage States
          </button>
          <button className="btn btn-primary me-2" onClick={() => navigate('/professions')}>
            Manage Professions
          </button>
          <button 
            className="btn btn-warning" 
            onClick={handleAutoArchive}
            disabled={loading}
            title="Auto-archive historical classes"
          >
            <i className="bi bi-archive me-2"></i>
            {loading ? 'Running...' : 'Auto-Archive'}
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="row mb-4">
        <div className="col-md-3">
          <div className="card bg-primary text-white">
            <div className="card-body">
              <h5 className="card-title">Total Users</h5>
              <h2>{stats.totalUsers}</h2>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card bg-success text-white">
            <div className="card-body">
              <h5 className="card-title">Active Users</h5>
              <h2>{stats.activeUsers}</h2>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card bg-warning text-white">
            <div className="card-body">
              <h5 className="card-title">Suspended</h5>
              <h2>{stats.suspendedUsers}</h2>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card bg-info text-white">
            <div className="card-body">
              <h5 className="card-title">Filtered Results</h5>
              <h2>{stats.filteredCount}</h2>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="card mb-4">
        <div className="card-header">
          <h5 className="mb-0">Filters</h5>
        </div>
        <div className="card-body">
          <div className="row g-3">
            <div className="col-md-3">
              <label className="form-label">State</label>
              <select 
                className="form-select" 
                value={filters.stateID || ''} 
                onChange={e => handleFilterChange('stateID', e.target.value ? parseInt(e.target.value) : undefined)}
              >
                <option value="">All States</option>
                {states.map(s => (
                  <option key={s.stateID} value={s.stateID}>
                    {s.state}
                  </option>
                ))}
              </select>
            </div>

            <div className="col-md-3">
              <label className="form-label">Profession</label>
              <select 
                className="form-select" 
                value={filters.professionID || ''} 
                onChange={e => handleFilterChange('professionID', e.target.value)}
              >
                <option value="">All Professions</option>
                {professions.filter(p => p.isActive !== false).map(p => (
                  <option key={p.professionID} value={p.professionID}>
                    {p.name} ({p.professionID})
                  </option>
                ))}
              </select>
            </div>

            <div className="col-md-3">
              <label className="form-label">Email</label>
              <input 
                type="text" 
                className="form-control" 
                placeholder="Search by email..." 
                value={filters.email || ''} 
                onChange={e => handleFilterChange('email', e.target.value)}
              />
            </div>

            <div className="col-md-3">
              <label className="form-label">Status</label>
              <select 
                className="form-select" 
                value={filters.suspended === undefined ? '' : filters.suspended.toString()} 
                onChange={e => handleFilterChange('suspended', e.target.value === '' ? undefined : e.target.value === 'true')}
              >
                <option value="">All Status</option>
                <option value="false">Active</option>
                <option value="true">Suspended</option>
              </select>
            </div>

            <div className="col-md-3">
              <label className="form-label">Role</label>
              <select 
                className="form-select" 
                value={filters.role || ''} 
                onChange={e => handleFilterChange('role', e.target.value)}
              >
                <option value="">All Roles</option>
                <option value="user">User</option>
                <option value="admin">Admin</option>
              </select>
            </div>

            <div className="col-md-3">
              <label className="form-label">Sort By</label>
              <select 
                className="form-select" 
                value={filters.sortBy || 'createdAt'} 
                onChange={e => handleFilterChange('sortBy', e.target.value)}
              >
                <option value="createdAt">Registration Date</option>
                <option value="lastLoginAt">Last Login</option>
                <option value="email">Email</option>
                <option value="firstName">First Name</option>
                <option value="lastName">Last Name</option>
              </select>
            </div>

            <div className="col-md-3">
              <label className="form-label">Order</label>
              <select 
                className="form-select" 
                value={filters.sortOrder || 'desc'} 
                onChange={e => handleFilterChange('sortOrder', e.target.value as 'asc' | 'desc')}
              >
                <option value="desc">Descending</option>
                <option value="asc">Ascending</option>
              </select>
            </div>

            <div className="col-md-3 d-flex align-items-end">
              <div className="form-check">
                <input
                  type="checkbox"
                  className="form-check-input"
                  id="showDeletedUsers"
                  checked={filters.showDeleted === true}
                  onChange={e => handleFilterChange('showDeleted', e.target.checked ? true : undefined)}
                />
                <label className="form-check-label" htmlFor="showDeletedUsers">
                  Show Deleted Users
                </label>
              </div>
            </div>

            <div className="col-md-3 d-flex align-items-end">
              <button className="btn btn-secondary w-100" onClick={clearFilters}>
                Clear Filters
              </button>
            </div>
          </div>

          {/* Active Filters Display */}
          {activeFilters.length > 0 && (
            <div className="mt-3">
              <small className="text-muted">Active filters: </small>
              {activeFilters.map(([key, value]) => (
                <span key={key} className="badge bg-info me-2">
                  {key}: {String(value)}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Results Header */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h5>
            Users ({stats.filteredCount} {stats.filteredCount === 1 ? 'result' : 'results'})
          </h5>
        </div>
        <div className="d-flex align-items-center gap-2">
          <label className="mb-0 me-2">Rows per page:</label>
          <select
            className="form-select form-select-sm"
            style={{ width: 'auto' }}
            value={filters.limit || 20}
            onChange={e => handleFilterChange('limit', parseInt(e.target.value))}
          >
            <option value={20}>20</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>
          <button className="btn btn-sm btn-outline-primary" onClick={exportToCSV} disabled={users.length === 0}>
            Export CSV
          </button>
        </div>
      </div>

      {/* Error Display */}
      {error && <div className="alert alert-danger">{error}</div>}

      {/* Loading */}
      {loading && (
        <div className="text-center py-5">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      )}

      {/* Users Table */}
      {!loading && users.length > 0 && (
        <>
          <div className="table-responsive">
            <table className="table cec-table table-hover table-sm mb-0">
              <thead>
                <tr>
                  <th>User</th>
                  <th className="d-none d-md-table-cell">Email</th>
                  <th className="d-none d-lg-table-cell">Plan</th>
                  <th className="d-none d-lg-table-cell">Profession</th>
                  <th>Role</th>
                  <th className="d-none d-sm-table-cell">Status</th>
                  <th className="d-none d-sm-table-cell">Registered</th>
                  <th className="d-none d-xl-table-cell">Last Login</th>
                  {filters.showDeleted && <th>Actions</th>}
                </tr>
              </thead>
              <tbody>
                {users.map(user => {
                  const userId = String(user.id || user._id);
                  const sub = user.subscription;
                  const plan = sub?.plan || 'free';
                  const tier = sub?.tier;
                  const iv = sub?.billingInterval === 'annual' ? 'Y' : 'M';
                  const { bg: profBg, color: profColor } = getProfessionBadgeStyle(user.professionID);
                  const lightBg = ['#FFFFFF','#E2F6EF','#FEF3C7','#8BF559','#FFCD00','#EDE8DE'].includes(profBg);
                  return (
                    <tr
                      key={userId}
                      onClick={() => navigate(`/admin/users/${userId}/view`)}
                      style={{ cursor: 'pointer' }}
                    >
                      <td>
                        <div className="d-flex flex-column">
                          <span>{`${user.firstName || ''} ${user.lastName || ''}`.trim() || 'N/A'}</span>
                          <small className="text-muted d-md-none">{user.email}</small>
                        </div>
                      </td>
                      <td className="d-none d-md-table-cell">{user.email}</td>
                      <td className="d-none d-lg-table-cell">
                        {sub?.status === 'trialing'
                          ? <span className="badge" style={{ background: '#FEF3C7', color: '#B45309', border: '1px solid #F59E0B' }} title="On the 60-day trial — not a paying subscriber">Trial</span>
                          : plan === 'free'
                          ? <span className="badge" style={{ background: '#E2F6EF', color: '#065f46' }}>Free</span>
                          : plan === 'pro'
                          ? <span className="badge" style={{ background: '#F59E0B', color: '#fff' }}>Pro-{iv}</span>
                          : <span className="badge" style={{ background: '#10B981', color: '#fff' }}>
                              {tier === 'starter' ? 'Starter' : tier === 'firm' ? 'Firm' : tier === 'practice' ? 'Practice' : 'Team'}-{iv}
                            </span>
                        }
                      </td>
                      <td className="d-none d-lg-table-cell">
                        <span
                          className="badge"
                          style={{
                            background: profBg,
                            color: profColor,
                            border: lightBg ? '1px solid #d1d5db' : 'none',
                          }}
                        >
                          {user.professionID || 'N/A'}
                        </span>
                      </td>
                      <td>
                        <span className={`badge ${user.role === 'admin' ? 'bg-danger' : 'bg-secondary'}`}>
                          {user.role === 'admin' ? 'Admin' : 'User'}
                        </span>
                      </td>
                      <td className="d-none d-sm-table-cell">
                        <span className={`badge ${(user as any).isDeleted ? 'bg-dark' : user.suspended ? 'bg-secondary' : 'bg-success'}`}>
                          {(user as any).isDeleted ? 'Deleted' : user.suspended ? 'Suspended' : 'Active'}
                        </span>
                      </td>
                      <td className="d-none d-sm-table-cell">
                        <span style={{ fontSize: '0.82rem', whiteSpace: 'nowrap' }}>
                          {fmtDateTime(user.createdAt)}
                        </span>
                      </td>
                      <td className="d-none d-xl-table-cell">
                        <span style={{ fontSize: '0.82rem', whiteSpace: 'nowrap', color: user.lastLoginAt ? 'inherit' : 'var(--bs-secondary)' }}>
                          {user.lastLoginAt ? fmtDateTime(user.lastLoginAt) : 'Never'}
                        </span>
                      </td>
                      {filters.showDeleted && (
                        <td>
                          <button
                            className="btn btn-sm btn-outline-success"
                            disabled={restoringId === userId}
                            onClick={e => handleRestoreUser(e, userId)}
                          >
                            {restoringId === userId ? '...' : 'Restore'}
                          </button>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {pagination.totalPages > 1 && (
            <nav>
              <ul className="pagination justify-content-center">
                <li className={`page-item ${pagination.page === 1 ? 'disabled' : ''}`}>
                  <button 
                    className="page-link" 
                    onClick={() => handlePageChange(pagination.page - 1)}
                    disabled={pagination.page === 1}
                  >
                    Previous
                  </button>
                </li>
                
                {[...Array(pagination.totalPages)].map((_, i) => {
                  const pageNum = i + 1;
                  if (
                    pageNum === 1 ||
                    pageNum === pagination.totalPages ||
                    (pageNum >= pagination.page - 2 && pageNum <= pagination.page + 2)
                  ) {
                    return (
                      <li key={pageNum} className={`page-item ${pagination.page === pageNum ? 'active' : ''}`}>
                        <button className="page-link" onClick={() => handlePageChange(pageNum)}>
                          {pageNum}
                        </button>
                      </li>
                    );
                  } else if (pageNum === pagination.page - 3 || pageNum === pagination.page + 3) {
                    return <li key={pageNum} className="page-item disabled"><span className="page-link">...</span></li>;
                  }
                  return null;
                })}
                
                <li className={`page-item ${pagination.page === pagination.totalPages ? 'disabled' : ''}`}>
                  <button 
                    className="page-link" 
                    onClick={() => handlePageChange(pagination.page + 1)}
                    disabled={pagination.page === pagination.totalPages}
                  >
                    Next
                  </button>
                </li>
              </ul>
            </nav>
          )}

          {/* Pagination Info */}
          <div className="text-center text-muted mb-4">
            Showing {((pagination.page - 1) * pagination.limit) + 1} to{' '}
            {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} users
          </div>
        </>
      )}

      {/* No Results */}
      {!loading && users.length === 0 && (
        <div className="alert alert-info text-center">
          No users found matching your filters.
        </div>
      )}
    </div>
  );
};

export default AdminPanel;
